import "react-toastify/dist/ReactToastify.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";
import TopNav from "../../components/TopNav/TopNav";
import SideBar from "../../components/SideBar/SideBar";
import Button from "../../components/Buttons/Button";
import ConfirmModal from "../../components/ConfirmModal/ConfirmModal";
import Skeleton from "../../components/Skeleton/Skeleton";
import './Refund.scss';

const ListRefundRequests = () => {
  const navigate = useNavigate();
  const [refunds, setRefunds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/refund-request", {
        params: { page: 1, records: 100, sort: "recent" },
      });
      setRefunds(response.data.data || []);
    } catch (error) {
      console.error("Error fetching disputes:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleReview = async () => {
    if (!confirmAction) return;
    try {
      await api.post(`/refund-request/${confirmAction.id}/review`, {
        status: confirmAction.type,
      });
      toast.success(`Dispute ${confirmAction.type}`);
      fetchData();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Failed to process dispute';
      toast.error(msg);
    } finally {
      setConfirmAction(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#2ECC71';
      case 'rejected': return '#E74C3C';
      case 'processed': return '#0A3FD6';
      default: return '#F1C40F';
    }
  };

  const formatType = (type) => {
    return type === 'ticket_sale' ? 'Ticket' : type === 'bottle_service' ? 'Bottle Service' : type;
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="disputes">
            <ConfirmModal
              isOpen={!!confirmAction}
              title={confirmAction?.type === 'approved' ? 'Approve Refund' : 'Reject Refund'}
              message={`${confirmAction?.type === 'approved' ? 'Approve' : 'Reject'} the refund of ${confirmAction?.amount} for ${confirmAction?.user}?`}
              confirmLabel={confirmAction?.type === 'approved' ? 'Approve' : 'Reject'}
              danger={confirmAction?.type === 'rejected'}
              onConfirm={handleReview}
              onCancel={() => setConfirmAction(null)}
            />

            <h1 className="disputes__title">Disputes</h1>

            {isLoading ? (
              <div className="disputes__loading">
                <Skeleton type="row" />
                <Skeleton type="row" />
                <Skeleton type="row" />
                <Skeleton type="row" />
              </div>
            ) : refunds.length === 0 ? (
              <div className="disputes__empty">
                <span className="iconify" data-icon="solar:shield-check-outline" style={{ fontSize: 48, color: 'rgba(255,255,255,0.12)' }}></span>
                <p>No disputes</p>
              </div>
            ) : (
              <div className="disputes__list">
                {refunds.map((refund) => {
                  const isExpanded = expandedId === refund.id;
                  const userName = `${refund.user?.firstName || ''} ${refund.user?.lastName || ''}`.trim();

                  return (
                    <div key={refund.id} className="disputes__card">
                      <div className="disputes__row" onClick={() => setExpandedId(isExpanded ? null : refund.id)}>
                        <div className="disputes__user">
                          <div className="disputes__name">{userName}</div>
                          <div className="disputes__meta">{refund.event_name || formatType(refund.type)}</div>
                        </div>
                        <div className="disputes__amount">${(refund.requested_amount || 0).toFixed(2)}</div>
                        <span className="disputes__type-badge">{formatType(refund.type)}</span>
                        <span className="disputes__status" style={{ color: getStatusColor(refund.status), background: getStatusColor(refund.status) + '18' }}>
                          {refund.status}
                        </span>
                        <div className="disputes__date">{new Date(refund.requested_at).toLocaleDateString()}</div>
                        <div className="disputes__expand" style={{ transform: isExpanded ? 'rotate(180deg)' : '' }}>
                          <span className="iconify" data-icon="solar:alt-arrow-down-outline" style={{ fontSize: 16 }}></span>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="disputes__details">
                          <div className="disputes__detail-grid">
                            <div>
                              <div className="disputes__detail-label">Reason</div>
                              <div className="disputes__detail-value">{refund.reason || 'No reason provided'}</div>
                            </div>
                            <div>
                              <div className="disputes__detail-label">Email</div>
                              <div className="disputes__detail-value">{refund.user?.email || '—'}</div>
                            </div>
                            <div>
                              <div className="disputes__detail-label">Event</div>
                              <div className="disputes__detail-value">
                                {refund.event_id ? (
                                  <span className="disputes__event-link" onClick={(e) => { e.stopPropagation(); navigate(`/events/edit/${refund.event_id}`); }}>
                                    {refund.event_name || 'View Event'}
                                  </span>
                                ) : (refund.event_name || '—')}
                              </div>
                            </div>
                            <div>
                              <div className="disputes__detail-label">Requested</div>
                              <div className="disputes__detail-value">{new Date(refund.requested_at).toLocaleString('en-US', { timeZone: 'America/New_York' })}</div>
                            </div>
                            {refund.transaction_id && (
                              <div>
                                <div className="disputes__detail-label">Transaction ID</div>
                                <div className="disputes__detail-value" style={{ fontFamily: 'monospace', fontSize: 12 }}>{refund.transaction_id}</div>
                              </div>
                            )}
                            {refund.admin_notes && (
                              <div>
                                <div className="disputes__detail-label">Admin Notes</div>
                                <div className="disputes__detail-value">{refund.admin_notes}</div>
                              </div>
                            )}
                          </div>

                          <div className="disputes__actions">
                            <button className="disputes__view-btn" onClick={(e) => { e.stopPropagation(); navigate(`/users/${refund.user?._id}`); }}>
                              View User
                            </button>
                            {refund.status === 'pending' && (
                              <>
                                <Button primaryBtn onClick={(e) => { e.stopPropagation(); setConfirmAction({ id: refund.id, type: 'approved', user: userName, amount: `$${(refund.requested_amount || 0).toFixed(2)}` }); }}>
                                  Approve
                                </Button>
                                <button className="disputes__reject-btn" onClick={(e) => { e.stopPropagation(); setConfirmAction({ id: refund.id, type: 'rejected', user: userName, amount: `$${(refund.requested_amount || 0).toFixed(2)}` }); }}>
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </article>
      </div>
    </main>
  );
};

export default ListRefundRequests;
